import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { ComponentProps, useCallback, useEffect, useRef } from 'react';
import { Map, MapRef, Popup, ScaleControl } from 'react-map-gl';
import useLegendStore from '../../../store/legendStore';
import useMapStore from '../../../store/mapStore';
import { useMarkerStore } from '../../../store/markerStore';
import { usePointStore } from '../../../store/pointStore';
import Footer from '../../layout/Footer/Footer';
import { PointPopup } from './Control/Popup';
import { LayerInfo, useMapInteractions } from './hooks/useMapInteractions';
import { useMapMeasurement } from './hooks/useMapMeasurement';
import { useMapViewport } from './hooks/useMapViewport';
import './Map.scss';
import {
  DeckGLOverlay,
  IMapProps,
  INITIAL_VIEW_STATE,
  MAPBOX_ACCESS_TOKEN,
  MAPBOX_STYLE_TOKEN,
} from './MapHelper';

const MapComponent = (props: IMapProps) => {
  const {
    popupContent,
    popupCoordinates,
    layers,
    onPopupCoordinatesChanged,
    onClosePopup,
    onClick,
    onMoveMouse,
    addMeasurementPoint,
  } = props;

  const dragPan = useMapStore((state) => state.dragPan);
  const updatedBounds = useMapStore((state) => state.updatedBounds);
  const setMapBoundsZoom = useMapStore((state) => state.setMapBoundsZoom);
  // const persistedZoom = useMapStore((s) => s.mapBoundsZoom.zoom);
  const setPersistedViewState = useMapStore((state) => state.setViewState);
  const persistedViewState = useMapStore((s) => s.viewState);

  const isAddMarkerMode = useMarkerStore((state) => state.isAddMarkerMode);

  const isMeasureMode = useLegendStore((state) => state.isMeasureMode);
  const { measurement } = useMapMeasurement();

  const mapRef = useRef<MapRef | null>(null);
  const mapLib = mapboxgl as unknown as NonNullable<
    ComponentProps<typeof Map>['mapLib']
  >;

  const setActivePopupPoint = usePointStore(
    (state) => state.setActivePopupPoint
  );

  const {
    viewState,
    handleViewportChange,
    handleOnLoad,
    handleOnResize,
    handleZoomPan,
  } = useMapViewport({
    initialViewState: {
      ...INITIAL_VIEW_STATE,
      longitude: persistedViewState.longitude ?? INITIAL_VIEW_STATE.longitude,
      latitude: persistedViewState.latitude ?? INITIAL_VIEW_STATE.latitude,
      zoom: persistedViewState.zoom ?? INITIAL_VIEW_STATE.zoom,
    },
    setPersistedViewState,
    setMapBoundsZoom,
    updatedBounds,
  });

  const {
    hoverInfoActivePoint,
    handleMouseMove,
    mouseCoords,
    mousePosition,
    handleHover,
    handleClick,
    getCursor,
  } = useMapInteractions({
    setActivePopupPoint,
    onPopupCoordinatesChanged,
    dragPan,
    isAddMarkerMode,
    isMeasureMode,
    onMeasurementPoint: addMeasurementPoint,
  });

  const handleDeckHover = useCallback(
    (info: unknown) => {
      const layerInfo = info as LayerInfo;

      handleHover(layerInfo);

      if (!isMeasureMode || !layerInfo?.coordinate || !onMoveMouse) {
        return;
      }

      onMoveMouse([layerInfo.coordinate[0], layerInfo.coordinate[1]]);
    },
    [handleHover, isMeasureMode, onMoveMouse]
  );

  useEffect(() => {
    const mapInstance = mapRef.current?.getMap();

    if (mapInstance) {
      window.__MOOD_E2E_MAP__ = mapInstance;
    }

    return () => {
      if (window.__MOOD_E2E_MAP__ === mapInstance) {
        window.__MOOD_E2E_MAP__ = undefined;
      }
    };
  }, [viewState.longitude, viewState.latitude, viewState.zoom]);

  const cursorHintText =
    isMeasureMode && !measurement.isClosed && mousePosition
      ? measurement.isSnapToFirst && measurement.points.length >= 3
        ? 'Close polygon'
        : measurement.points.length === 0
        ? 'Add first point'
        : null
      : null;

  return (
    <div
      className={
        isAddMarkerMode ? 'map-container adding-marker' : 'map-container'
      }
      data-testid='AppContainer_Map'
    >
      <Map
        key='map'
        ref={mapRef}
        antialias
        renderWorldCopies
        mapLib={mapLib}
        mapboxAccessToken={MAPBOX_ACCESS_TOKEN}
        mapStyle={MAPBOX_STYLE_TOKEN}
        dragPan={dragPan}
        onMove={handleViewportChange}
        onMouseMove={handleMouseMove}
        onZoomEnd={handleZoomPan}
        onDragEnd={handleZoomPan}
        onLoad={handleOnLoad}
        onResize={handleOnResize}
        pitchWithRotate={false}
        style={{ position: 'fixed', width: '100vw', height: '100vh' }}
        {...viewState}
      >
        <DeckGLOverlay
          layers={[...layers]}
          interleaved={true}
          getCursor={getCursor}
          onHover={handleDeckHover}
          onClick={(info, ev) => {
            const clickCoordinate =
              info.coordinate && info.coordinate.length === 2
                ? ([info.coordinate[0], info.coordinate[1]] as [number, number])
                : undefined;

            handleClick(
              info as LayerInfo,
              ev,
              clickCoordinate,
              onClick,
              isAddMarkerMode
            );
          }}
        />
        {popupContent && popupCoordinates !== null && (
          <PointPopup
            popup={{ coordinates: popupCoordinates, onClose: onClosePopup }}
          >
            {popupContent}
          </PointPopup>
        )}
        <ScaleControl
          unit='metric'
          maxWidth={150}
          style={{ pointerEvents: 'none' }}
        />
        {hoverInfoActivePoint && (
          <Popup
            longitude={hoverInfoActivePoint?.coordinates?.[0]}
            latitude={hoverInfoActivePoint?.coordinates?.[1]}
            closeButton={false}
            anchor='top'
            offset={30}
          >
            <div>Click and hold to move point</div>
          </Popup>
        )}
      </Map>

      {cursorHintText && mousePosition && (
        <div
          className='measure-cursor-tooltip'
          style={{
            left: `${mousePosition[0] + 16}px`,
            top: `${mousePosition[1] + 16}px`,
          }}
        >
          {cursorHintText}
        </div>
      )}

      <Footer mouseCoords={mouseCoords} />
    </div>
  );
};

export default MapComponent;
